import { type ObjectId } from 'mongodb';
import { Vals } from '~lib';
import { MaybeDocId } from '../types';

/**
 * @desc A user's instant verdict on one agent response (H2-205): thumbs up or down, optionally
 * followed by a free-text comment.
 *
 * One record per `(userId, conversationId, messageId)` — the verdict is written the moment the
 * thumb is pressed and the comment, if any, is folded into the same record afterwards, so a
 * thumbs-down with no comment is a complete record rather than a half-finished one.
 */
export namespace ResponseFeedback {
  export const MAX_COMMENT_LENGTH = 2000;

  export const verdicts = {
    Up: 'up',
    Down: 'down',
  } as const;
  export type Verdict = Vals<typeof verdicts>;
  export const ALL_VERDICTS = Object.values(verdicts) as [Verdict, ...Verdict[]];

  export type Model<
    OId extends string | ObjectId = ObjectId,
    D extends string | Date = Date,
    TDocId extends void | string | ObjectId = OId,
  > = MaybeDocId<TDocId> & {
    refs: {
      /** @desc Internal user id of the person giving the feedback — the conversation's owner. */
      userId: string;
      conversationId: OId;
      /** @desc The `id` of the model `ClaudeMessage` history event being rated. */
      messageId: string;
    };
    verdict: Verdict;
    /** @desc Null until the user adds one; only ever set after a verdict exists. */
    comment: string | null;
    createdAt: D;
    updatedAt: D;
  };

  export const toSerial = (f: Model): Model<string, string, string> => ({
    _id: f._id.toString(),
    refs: { ...f.refs, conversationId: f.refs.conversationId.toString() },
    verdict: f.verdict,
    comment: f.comment,
    createdAt: f.createdAt.toISOString(),
    updatedAt: f.updatedAt.toISOString(),
  });

  export type Serial = Model<string, string, string>;

  /** @desc What the chat needs to know about a conversation's existing feedback: which responses already have a verdict. */
  export type Given = Pick<Serial, '_id' | 'verdict' | 'comment'> & { messageId: string };

  /**
   * @desc One of a user's records as the admin per-user feedback list shows it: the record plus
   * what it rated, resolved from the conversation in the same query — the conversation's title
   * (`null` when it has none or no longer exists), the rated message's `turnId` (so the inspector
   * can open that turn) and the start of the rated response's text.
   */
  export type AdminItem = Serial & {
    conversationTitle: string | null;
    conversationDeleted: boolean;
    turnId: string | null;
    responseSnippet: string | null;
  };

  /** @desc Upper bound on how much of the rated response `AdminItem.responseSnippet` carries. */
  export const SNIPPET_LENGTH = 240;

  /** @desc Positive/negative totals over one trailing window. */
  export type Counts = { up: number; down: number };
  export type Stats = { week: Counts; month: Counts };
}
